import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  RiArrowLeftLine,
  RiBuilding2Line,
  RiUserLine,
  RiShieldUserLine,
  RiBriefcaseLine,
  RiPhoneLine,
  RiMailLine,
  RiMapPinLine,
  RiNewspaperLine,
  RiAddLine,
  RiCloseLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiPriceTag3Line,
  RiListCheck2,
  RiHashtag,
  RiMoneyDollarCircleLine,
  RiDeleteBinLine,
  RiCalendarLine,
  RiRefreshLine,
  RiEditLine,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const BASE_URL = `${process.env.REACT_APP_API_URL}/media`;

const Spinner = ({ size = "w-4 h-4" }) => (
  <AiOutlineLoading3Quarters className={`animate-spin ${size}`} />
);

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
      ${
        type === "success"
          ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
          : "bg-red-50 border border-red-200 text-red-800"
      }`}
    >
      {type === "success" ? (
        <RiCheckLine className="w-4 h-4 text-emerald-500" />
      ) : (
        <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      )}
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

const Modal = ({ title, onClose, children, maxWidth = "max-w-lg" }) =>
  createPortal(
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={`relative z-10 w-full ${maxWidth} mx-4 bg-white border border-blue-100 rounded-2xl shadow-2xl overflow-hidden`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100 bg-blue-50">
          <h2 className="text-base font-semibold text-blue-900 flex items-center gap-2">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-blue-400 hover:text-blue-700 transition rounded-lg p-1 hover:bg-blue-100"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 max-h-[80vh] overflow-y-auto bg-white">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );

const TagInput = ({ tags, onChange }) => {
  const [input, setInput] = useState("");

  const addTag = () => {
    const trimmed = input.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
    }
    setInput("");
  };

  const removeTag = (idx) => onChange(tags.filter((_, i) => i !== idx));

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2 min-h-[38px] bg-blue-50/60 border border-blue-200 rounded-lg p-2">
        {tags.map((tag, i) => (
          <span
            key={i}
            className="flex items-center gap-1 bg-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-1 rounded-md"
          >
            {tag}
            <button
              onClick={() => removeTag(i)}
              className="hover:text-red-500 transition ml-0.5"
            >
              <RiCloseLine className="w-3 h-3" />
            </button>
          </span>
        ))}
        {tags.length === 0 && (
          <span className="text-xs text-blue-300 self-center pl-1">
            No names added yet…
          </span>
        )}
      </div>
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addTag();
            }
          }}
          placeholder="Type a name and press Enter or Add"
          className="flex-1 bg-blue-50/60 border border-blue-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
        />
        <button
          onClick={addTag}
          type="button"
          className="px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition"
        >
          Add
        </button>
      </div>
    </div>
  );
};

const defaultAdTypeForm = { typeNumber: "", price: "", names: [] };

const typeColors = [
  "bg-blue-100 text-blue-700 border-blue-200",
  "bg-violet-100 text-violet-700 border-violet-200",
  "bg-emerald-100 text-emerald-700 border-emerald-200",
  "bg-amber-100 text-amber-700 border-amber-200",
  "bg-rose-100 text-rose-700 border-rose-200",
  "bg-cyan-100 text-cyan-700 border-cyan-200",
  "bg-orange-100 text-orange-700 border-orange-200",
];

const getTypeColor = (idx) => typeColors[idx % typeColors.length];

const InfoCard = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 p-3.5 rounded-xl bg-blue-50/60 border border-blue-100">
    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
      <Icon className="w-4 h-4 text-blue-500" />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-widest mb-0.5">
        {label}
      </p>
      <p className="text-sm text-slate-700 font-medium break-words">
        {value || "—"}
      </p>
    </div>
  </div>
);

export default function MediaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adTypeForm, setAdTypeForm] = useState(defaultAdTypeForm);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState(null);
  const [editingAdType, setEditingAdType] = useState(null);

  const notify = (message, type = "success") => setToast({ message, type });

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/get/${id}`);
      setMedia(res.data?.data || res.data);
    } catch {
      notify("Failed to load media details", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia();
  }, [id]);

  const openAddType = () => {
    setAdTypeForm(defaultAdTypeForm);
    setModal("addType");
  };

  const openDeleteType = (adType) => {
    setSelectedType(adType);
    setModal("deleteType");
  };

  const closeModal = () => {
    setModal(null);
    setSelectedType(null);
    setEditingAdType(null);
    setAdTypeForm(defaultAdTypeForm);
  };

  const handleAdTypeFormChange = (e) => {
    setAdTypeForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleAddType = async () => {
    if (
      !adTypeForm.typeNumber ||
      !adTypeForm.price ||
      adTypeForm.names.length === 0
    ) {
      notify("Please fill all fields and add at least one name", "error");
      return;
    }
    setActionLoading(true);
    try {
      await axios.patch(`${BASE_URL}/add-adtype/${id}`, {
        typeNumber: Number(adTypeForm.typeNumber),
        price: Number(adTypeForm.price),
        names: adTypeForm.names,
      });
      notify("Ad type added successfully");
      fetchMedia();
      closeModal();
    } catch {
      notify("Failed to add ad type", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteType = async () => {
    setActionLoading(true);
    try {
      await axios.patch(`${BASE_URL}/adtype/delete/${id}`, {
        adTypeId: selectedType._id,
      });
      notify("Ad type removed successfully");
      fetchMedia();
      closeModal();
    } catch (error) {
      notify(
        error.response?.data?.message || "Failed to remove ad type",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const openEditType = (adType) => {
    setEditingAdType(adType);
    setAdTypeForm({
      typeNumber: adType.typeNumber,
      price: adType.price,
      names: [...adType.names],
    });
    setModal("editType");
  };

  const handleEditType = async () => {
    if (
      !adTypeForm.typeNumber ||
      !adTypeForm.price ||
      adTypeForm.names.length === 0
    ) {
      notify("Please fill all fields and add at least one name", "error");
      return;
    }
    setActionLoading(true);
    try {
      await axios.patch(`${BASE_URL}/adtype/update/${id}`, {
        adTypeId: editingAdType._id,
        typeNumber: Number(adTypeForm.typeNumber),
        price: Number(adTypeForm.price),
        names: adTypeForm.names,
      });
      notify("Ad type updated successfully");
      fetchMedia();
      closeModal();
    } catch (error) {
      notify(
        error.response?.data?.message || "Failed to update ad type",
        "error",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-blue-400">
          <Spinner size="w-8 h-8" />
          <span className="text-sm font-medium">Loading media details…</span>
        </div>
      </div>
    );
  }

  if (!media) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center">
            <RiErrorWarningLine className="w-8 h-8 text-red-400" />
          </div>
          <p className="text-slate-500 text-sm font-medium">Media not found.</p>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-semibold"
          >
            <RiArrowLeftLine className="w-4 h-4" /> Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      {/* Header */}
      <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 text-blue-400 hover:text-blue-700 hover:bg-blue-50 transition"
            >
              <RiArrowLeftLine className="w-4 h-4" />
            </button>
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white text-sm font-bold shadow-md shadow-blue-200">
              {(media.organisation || "?")[0].toUpperCase()}
            </div>
            <div>
              <h1 className="text-base font-bold text-blue-800 tracking-tight leading-tight">
                {media.organisation}
              </h1>
              <p className="text-[11px] text-blue-400 tracking-widest uppercase">
                Media Details
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchMedia}
              title="Refresh"
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition"
            >
              <RiRefreshLine className="w-4 h-4" />
            </button>
            <button
              onClick={openAddType}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-all shadow-md shadow-blue-200"
            >
              <RiAddLine className="w-4 h-4" />
              Add Ad Type
            </button>
          </div>
        </div>
      </header>

      <div className="w-full px-6 pt-6 pb-12 mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT — Contact Info */}
          <div className="lg:col-span-1 flex flex-col gap-4">
            {/* Identity card */}
            <div className="bg-white border border-blue-100 rounded-2xl shadow-sm overflow-hidden">
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 px-6 py-6 flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-white text-2xl font-bold shadow-lg mb-3">
                  {(media.organisation || "?")[0].toUpperCase()}
                </div>
                <h2 className="text-white font-bold text-lg leading-tight">
                  {media.organisation}
                </h2>
                <p className="text-blue-200 text-xs mt-1">
                  {media.departmentOffice || ""}
                </p>
                <div className="mt-3 flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1">
                  <RiNewspaperLine className="w-3.5 h-3.5 text-blue-200" />
                  <span className="text-xs text-blue-100 font-medium">
                    {(media.adTypes || []).length} Ad Type
                    {(media.adTypes || []).length !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>
              <div className="p-4 flex flex-col gap-3">
                <InfoCard
                  icon={RiUserLine}
                  label="Contact Person"
                  value={media.contactPerson}
                />
                <InfoCard
                  icon={RiShieldUserLine}
                  label="Designation"
                  value={media.designation}
                />
                <InfoCard
                  icon={RiBriefcaseLine}
                  label="Department"
                  value={media.departmentOffice}
                />
                <InfoCard
                  icon={RiPhoneLine}
                  label="Phone"
                  value={media.phone}
                />
                <InfoCard icon={RiMailLine} label="Email" value={media.email} />
                <InfoCard
                  icon={RiMapPinLine}
                  label="Address"
                  value={media.address}
                />
              </div>
              {/* Timestamps */}
              <div className="px-4 pb-4 flex flex-col gap-1.5">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <RiCalendarLine className="w-3.5 h-3.5" />
                  <span>Created: {formatDate(media.createdAt)}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <RiCalendarLine className="w-3.5 h-3.5" />
                  <span>Updated: {formatDate(media.updatedAt)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Ad Types */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            <div className="bg-white border border-blue-100 rounded-2xl shadow-sm overflow-hidden">
              <div className="px-5 py-3.5 border-b border-blue-50 bg-blue-50/50 flex items-center justify-between">
                <span className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                  <RiPriceTag3Line className="w-4 h-4 text-blue-500" />
                  Advertisement Types
                </span>
                <span className="text-xs text-blue-400 font-medium bg-blue-100 px-2.5 py-0.5 rounded-full">
                  {(media.adTypes || []).length} types
                </span>
              </div>

              {!media.adTypes || media.adTypes.length === 0 ? (
                <div className="py-20 flex flex-col items-center gap-4 text-center">
                  <div className="w-14 h-14 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                    <RiPriceTag3Line className="w-7 h-7 text-blue-300" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-500">
                      No ad types yet
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Click "Add Ad Type" to get started.
                    </p>
                  </div>
                  <button
                    onClick={openAddType}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-4 py-2 rounded-lg transition shadow-md shadow-blue-200"
                  >
                    <RiAddLine className="w-4 h-4" /> Add Ad Type
                  </button>
                </div>
              ) : (
                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {media.adTypes.map((adType, idx) => (
                    <div
                      key={adType._id || idx}
                      className="border border-blue-100 rounded-xl overflow-hidden hover:shadow-md transition group"
                    >
                      {/* Type header */}
                      <div className="flex items-center justify-between px-4 py-3 bg-blue-50/70 border-b border-blue-100">
                        <div className="flex items-center gap-2">
                          <span
                            className={`flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full border ${getTypeColor(idx)}`}
                          >
                            <RiHashtag className="w-3 h-3" />
                            Type {adType.typeNumber}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="flex items-center gap-1 text-sm font-bold text-emerald-600">
                            <RiMoneyDollarCircleLine className="w-4 h-4" />₹
                            {adType.price}
                            <span className="text-[10px] text-slate-400 font-normal">
                              /sq cm
                            </span>
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="flex items-center gap-1 text-sm font-bold text-emerald-600">
                              <RiMoneyDollarCircleLine className="w-4 h-4" />₹
                              {adType.price}
                              <span className="text-[10px] text-slate-400 font-normal">
                                /sq cm
                              </span>
                            </span>
                            <button
                              onClick={() => openEditType(adType)}
                              title="Edit Ad Type"
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition opacity-0 group-hover:opacity-100"
                            >
                              <RiEditLine className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => openDeleteType(adType)}
                              title="Remove Ad Type"
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition opacity-0 group-hover:opacity-100"
                            >
                              <RiDeleteBinLine className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                      {/* Names */}
                      <div className="px-4 py-3 bg-white">
                        <div className="flex items-center gap-1.5 mb-2.5">
                          <RiListCheck2 className="w-3.5 h-3.5 text-blue-400" />
                          <span className="text-[10px] font-semibold text-blue-400 uppercase tracking-widest">
                            Ad Names ({adType.names?.length || 0})
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {(adType.names || []).map((name, ni) => (
                            <span
                              key={ni}
                              className="text-xs bg-slate-50 border border-slate-200 text-slate-600 px-2.5 py-1 rounded-md font-medium"
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Ad Type Modal */}
      {modal === "addType" && (
        <Modal
          title={
            <>
              <RiAddLine className="w-5 h-5 text-blue-500" /> Add Ad Type
            </>
          }
          onClose={closeModal}
        >
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Type Number */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                  Type Number
                </label>
                <div className="relative">
                  <RiHashtag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                  <input
                    type="number"
                    name="typeNumber"
                    value={adTypeForm.typeNumber}
                    onChange={handleAdTypeFormChange}
                    placeholder="e.g. 6"
                    className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                  />
                </div>
              </div>
              {/* Price */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                  Price per Sq Cm (₹)
                </label>
                <div className="relative">
                  <RiMoneyDollarCircleLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                  <input
                    type="number"
                    name="price"
                    value={adTypeForm.price}
                    onChange={handleAdTypeFormChange}
                    placeholder="e.g. 150"
                    className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                  />
                </div>
              </div>
            </div>
            {/* Names */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Ad Names
              </label>
              <TagInput
                tags={adTypeForm.names}
                onChange={(names) => setAdTypeForm((f) => ({ ...f, names }))}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleAddType}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}
              Add Ad Type
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Ad Type Modal */}
      {modal === "editType" && (
        <Modal
          title={
            <>
              <RiEditLine className="w-5 h-5 text-blue-500" /> Edit Ad Type
            </>
          }
          onClose={closeModal}
        >
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Type Number */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                  Type Number
                </label>
                <div className="relative">
                  <RiHashtag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                  <input
                    type="number"
                    name="typeNumber"
                    value={adTypeForm.typeNumber}
                    onChange={handleAdTypeFormChange}
                    placeholder="e.g. 6"
                    className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                  />
                </div>
              </div>
              {/* Price */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                  Price per Sq Cm (₹)
                </label>
                <div className="relative">
                  <RiMoneyDollarCircleLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
                  <input
                    type="number"
                    name="price"
                    value={adTypeForm.price}
                    onChange={handleAdTypeFormChange}
                    placeholder="e.g. 150"
                    className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
                  />
                </div>
              </div>
            </div>
            {/* Names */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
                Ad Names
              </label>
              <TagInput
                tags={adTypeForm.names}
                onChange={(names) => setAdTypeForm((f) => ({ ...f, names }))}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleEditType}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}
              Update Ad Type
            </button>
          </div>
        </Modal>
      )}

      {/* Delete Ad Type Modal */}
      {modal === "deleteType" && (
        <Modal
          title={
            <>
              <RiDeleteBinLine className="w-5 h-5 text-red-500" /> Remove Ad
              Type
            </>
          }
          onClose={closeModal}
        >
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <RiErrorWarningLine className="w-8 h-8 text-red-400" />
            </div>
            <p className="text-slate-700 font-semibold mb-1 text-base">
              Remove{" "}
              <span className="text-red-500">
                Type {selectedType?.typeNumber}
              </span>
              ?
            </p>
            <p className="text-slate-400 text-sm mb-2">
              This will permanently delete this ad type and all its associated
              names.
            </p>
            {selectedType?.names?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 justify-center mb-6 mt-3">
                {selectedType.names.map((n, i) => (
                  <span
                    key={i}
                    className="text-xs bg-red-50 border border-red-200 text-red-500 px-2 py-0.5 rounded-md"
                  >
                    {n}
                  </span>
                ))}
              </div>
            )}
            <div className="flex justify-center gap-3 mt-4">
              <button
                onClick={closeModal}
                className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
              >
                <RiCloseLine className="w-4 h-4" /> Cancel
              </button>
              <button
                onClick={handleDeleteType}
                disabled={actionLoading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition shadow-md shadow-red-100 disabled:opacity-60"
              >
                {actionLoading ? (
                  <Spinner />
                ) : (
                  <RiDeleteBinLine className="w-4 h-4" />
                )}
                Remove
              </button>
            </div>
          </div>
        </Modal>
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
